import { router } from '@inertiajs/react'
import { type ReactNode, useEffect, useState } from 'react'
import { Copy, Play, RefreshCw, Server, WandSparkles, Check, AlertCircle, Circle, Wrench, Trash2, Square } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import type { MigrationSource } from '../types'

interface Props {
  worker: { id: string; instance_name: string; organization_slug: string }
  migrationTarget: {
    version: string
    label: string
    skill_name: string
    skill_source?: string
    skill_path?: string
    skill_container_path?: string
    provider: string
    plugin_id: string
  }
  fleet_machines: { slug: string; name: string }[]
  currentMachineSlug: string | null
  isLegacySource: boolean
}

const APPROVED_SCOPES = [
  { key: 'credentials', label: 'Credenciais' },
  { key: 'channels', label: 'Canais' },
  { key: 'sessions', label: 'Sessões' },
  { key: 'people_memory', label: 'Memória e pessoas' },
  { key: 'cron', label: 'Rotinas' },
]

type Tone = 'info' | 'success' | 'warning' | 'critical' | 'attention' | 'neutral'
type TargetOperationKey = 'doctor_check' | 'doctor_repair' | 'start' | 'stop' | 'restart' | 'abort'

interface TargetOperationState {
  jobId: number | null
  status: string | null
  stdoutTail: string
  stderrTail: string
  error: string | null
  command: string | null
}

interface MigrationApplyState {
  jobId: number | null
  status: string | null
  stdoutTail: string
  stderrTail: string
  error: string | null
  command: string | null
  startedAt: string | null
  completedAt: string | null
}

const EMPTY_TARGET_OPERATION: TargetOperationState = {
  jobId: null,
  status: null,
  stdoutTail: '',
  stderrTail: '',
  error: null,
  command: null,
}

const EMPTY_MIGRATION_APPLY: MigrationApplyState = {
  jobId: null,
  status: null,
  stdoutTail: '',
  stderrTail: '',
  error: null,
  command: null,
  startedAt: null,
  completedAt: null,
}

interface FinalizeEvent {
  id?: number
  at?: string
  event?: string
  step?: string
  status?: string
  message?: string
  error?: string
  error_class?: string
  details?: Record<string, unknown>
}

interface FinalizeWorkflowStep {
  key: string
  title: string
  status?: string
  message?: string
  at?: string
  event?: string
  error?: string
  details?: Record<string, unknown>
  history: FinalizeEvent[]
}

interface FinalizeRunState {
  runId: string | null
  activeJobId: string | null
  status: string | null
  events: FinalizeEvent[]
  error: string | null
}

const EMPTY_FINALIZE_RUN: FinalizeRunState = {
  runId: null,
  activeJobId: null,
  status: null,
  events: [],
  error: null,
}

const FINALIZE_STEP_ORDER = [
  'enqueue',
  'validate_target',
  'validate_bootstrap',
  'validate_cutover',
  'stop_previous',
  'restart_target',
  'probe_slack',
  'probe_whatsapp',
  'switch_worker_pointer',
  'recovery',
  'done',
  'failure',
]

const FINALIZE_STEP_TITLES: Record<string, string> = {
  enqueue: 'Enfileirar cutover',
  validate_target: 'Validar destino',
  validate_bootstrap: 'Validar bootstrap',
  validate_cutover: 'Validar checklist',
  stop_previous: 'Parar instância antiga',
  restart_target: 'Preparar destino',
  probe_slack: 'Verificar Slack',
  probe_whatsapp: 'Verificar WhatsApp',
  switch_worker_pointer: 'Trocar apontamento',
  recovery: 'Rollback operacional',
  done: 'Concluir cutover',
  failure: 'Falha final',
}

interface CutoverChecklistItem {
  id: string
  message: string
  status: 'passed' | 'warning' | 'blocked'
  bypassable?: boolean
}

interface CutoverChecklist {
  items: CutoverChecklistItem[]
  blocked_count?: number
  warning_count?: number
}

interface ConfirmationDialogState {
  title: string
  description: string
  details?: ReactNode
  confirmLabel?: string
  cancelLabel?: string | null
  destructive?: boolean
  onConfirm?: () => void
}

const TARGET_OPERATION_KEYS: TargetOperationKey[] = ['doctor_check', 'doctor_repair', 'start', 'stop', 'restart', 'abort']

const TARGET_OPERATION_META: Record<TargetOperationKey, { title: string; endpoint: string; command: string }> = {
  doctor_check: {
    title: 'Doctor check',
    endpoint: 'migration_doctor_check',
    command: 'openclaw doctor --non-interactive',
  },
  doctor_repair: {
    title: 'Doctor repair',
    endpoint: 'migration_doctor_repair',
    command: 'ocf doctor-repair',
  },
  start: {
    title: 'Start destino',
    endpoint: 'migration_start',
    command: 'ocf start',
  },
  stop: {
    title: 'Stop destino',
    endpoint: 'migration_stop',
    command: 'ocf stop',
  },
  restart: {
    title: 'Restart destino',
    endpoint: 'migration_restart',
    command: 'ocf restart',
  },
  abort: {
    title: 'Abortar migração',
    endpoint: 'abort_migration',
    command: `ocf remove`,
  },
}

const PILL_CLASSES: Record<Tone, string> = {
  info: 'bg-blue-100 text-blue-700 dark:bg-blue-500/15 dark:text-blue-400',
  success: 'bg-green-100 text-green-700 dark:bg-green-500/15 dark:text-green-400',
  warning: 'bg-amber-100 text-amber-700 dark:bg-yellow-500/15 dark:text-yellow-400',
  critical: 'bg-red-100 text-red-700 dark:bg-red-500/15 dark:text-red-400',
  attention: 'bg-orange-100 text-orange-700 dark:bg-orange-500/15 dark:text-orange-400',
  neutral: 'bg-accent text-muted-foreground',
}

function Pill({ tone, children, title }: { tone: Tone; children: ReactNode; title?: string }) {
  return (
    <span title={title} className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${PILL_CLASSES[tone]}`}>
      {children}
    </span>
  )
}

function CardSection({ title, action, children }: { title: ReactNode; action?: ReactNode; children: ReactNode }) {
  return (
    <section className="bg-card border border-border rounded-lg overflow-hidden">
      <header className="flex items-center justify-between gap-3 px-4 py-3 bg-muted border-b border-border">
        <h3 className="text-sm font-semibold text-foreground">{title}</h3>
        {action}
      </header>
      <div className="p-4 space-y-4">{children}</div>
    </section>
  )
}

async function parseJsonResponse(res: Response) {
  const text = await res.text()
  if (!text) return {}

  try {
    return JSON.parse(text) as Record<string, unknown>
  } catch {
    return {
      ok: false,
      error: `Resposta inválida do servidor (${res.status}). Recarregue a página; se persistir, confira os logs.`,
    }
  }
}

function responseError(data: Record<string, unknown>, fallback: string) {
  return typeof data.error === 'string' && data.error.trim() ? data.error : fallback
}

function finalizeStepKey(event: FinalizeEvent) {
  if (event.step) return event.step
  if (event.event === 'fleet.migration.finalize_enqueued') return 'enqueue'
  if (event.event === 'fleet.migration.finalize_failed') return 'failure'
  return event.event || `event-${event.id || 'unknown'}`
}

function finalizeStepTitle(key: string, event?: FinalizeEvent) {
  return FINALIZE_STEP_TITLES[key] || event?.message || event?.event || key
}

function finalizeStepOrder(key: string) {
  const index = FINALIZE_STEP_ORDER.indexOf(key)
  return index === -1 ? FINALIZE_STEP_ORDER.length : index
}

function mergeFinalizeWorkflow(events: FinalizeEvent[]) {
  const steps = new Map<string, FinalizeWorkflowStep & { firstIndex: number }>()

  events.forEach((event, index) => {
    const key = finalizeStepKey(event)
    const current = steps.get(key)
    const history = current ? [...current.history, event] : [event]
    steps.set(key, {
      key,
      title: finalizeStepTitle(key, event),
      status: event.status || current?.status,
      message: event.message || current?.message,
      at: event.at || current?.at,
      event: event.event || current?.event,
      error: event.error || current?.error,
      details: event.details || current?.details,
      history,
      firstIndex: current?.firstIndex ?? index,
    })
  })

  return Array.from(steps.values()).sort((a, b) => {
    const byWorkflow = finalizeStepOrder(a.key) - finalizeStepOrder(b.key)
    return byWorkflow === 0 ? a.firstIndex - b.firstIndex : byWorkflow
  })
}

export default function MigrateTab({
  worker,
  migrationTarget,
  fleet_machines,
  currentMachineSlug,
  isLegacySource,
}: Props) {
  const targets = fleet_machines.filter((m) => m.slug !== currentMachineSlug)
  const savedTarget = readSavedTarget(worker.id)
  const savedTargetSlug = targets.some((m) => m.slug === savedTarget?.targetSlug) ? savedTarget?.targetSlug : null
  const [targetSlug, setTargetSlug] = useState(savedTargetSlug || targets[0]?.slug || '')
  const [targetOrgSlug, setTargetOrgSlug] = useState(savedTarget?.targetOrgSlug || worker.organization_slug)
  const [sources, setSources] = useState<MigrationSource[]>([])
  const orgSources = sources.filter((s) => s.org === targetOrgSlug || s.org === null)
  const visibleSources = orgSources.length > 0 ? orgSources : sources
  const initialSource = visibleSources[0] || null
  const hasSources = sources.length > 0

  const [preparing, setPreparing] = useState(false)
  const [loadingSources, setLoadingSources] = useState(false)
  const [sourceError, setSourceError] = useState<string | null>(null)
  const [prepareNotice, setPrepareNotice] = useState<string | null>(null)
  const [finalizing, setFinalizing] = useState(false)
  const [finalizeRun, setFinalizeRun] = useState<FinalizeRunState>(EMPTY_FINALIZE_RUN)
  const [targetOperations, setTargetOperations] = useState<Record<TargetOperationKey, TargetOperationState>>({
    doctor_check: EMPTY_TARGET_OPERATION,
    doctor_repair: EMPTY_TARGET_OPERATION,
    start: EMPTY_TARGET_OPERATION,
    stop: EMPTY_TARGET_OPERATION,
    restart: EMPTY_TARGET_OPERATION,
    abort: EMPTY_TARGET_OPERATION,
  })
  const [targetOperationRunning, setTargetOperationRunning] = useState<TargetOperationKey | null>(null)
  const [cutoverChecking, setCutoverChecking] = useState(false)
  const [cutoverChecklist, setCutoverChecklist] = useState<CutoverChecklist | null>(null)
  const [cutoverCheckError, setCutoverCheckError] = useState<string | null>(null)
  const [bypassSlackProbe, setBypassSlackProbe] = useState(false)
  const [selectedSource, setSelectedSource] = useState<MigrationSource | null>(initialSource)
  const [includeSecrets, setIncludeSecrets] = useState(true)
  const [scope, setScope] = useState<Record<string, boolean>>({})
  const [confirmationDialog, setConfirmationDialog] = useState<ConfirmationDialogState | null>(null)

  const [dryRunJobId, setDryRunJobId] = useState<number | null>(null)
  const [dryRunJobStatus, setDryRunJobStatus] = useState<string | null>(null)
  const [dryRunJobError, setDryRunJobError] = useState<string | null>(null)
  const [dryRunStdoutTail, setDryRunStdoutTail] = useState('')
  const [dryRunStderrTail, setDryRunStderrTail] = useState('')
  const [dryRunReport, setDryRunReport] = useState<Record<string, unknown> | null>(null)
  const [dryRunAt, setDryRunAt] = useState<string | null>(null)
  const [running, setRunning] = useState(false)
  const [applyState, setApplyState] = useState<MigrationApplyState>(EMPTY_MIGRATION_APPLY)
  const [applying, setApplying] = useState(false)

  const [handoffMd, setHandoffMd] = useState<string | null>(null)
  const [handoffId, setHandoffId] = useState<string | null>(null)
  const [copied, setCopied] = useState(false)

  const csrfToken = () => {
    const meta = document.querySelector('meta[name="csrf-token"]')
    return meta?.getAttribute('content') || ''
  }

  const resetTargetOperations = () => {
    setTargetOperations({
      doctor_check: EMPTY_TARGET_OPERATION,
      doctor_repair: EMPTY_TARGET_OPERATION,
      start: EMPTY_TARGET_OPERATION,
      stop: EMPTY_TARGET_OPERATION,
      restart: EMPTY_TARGET_OPERATION,
      abort: EMPTY_TARGET_OPERATION,
    })
  }

  const resetCutoverChecklist = () => {
    setCutoverChecklist(null)
    setCutoverCheckError(null)
  }

  const resetApply = () => {
    setApplyState(EMPTY_MIGRATION_APPLY)
    setApplying(false)
  }

  const updateTargetOperation = (key: TargetOperationKey, patch: Partial<TargetOperationState>) => {
    setTargetOperations((current) => ({
      ...current,
      [key]: { ...EMPTY_TARGET_OPERATION, ...current[key], ...patch },
    }))
  }

  const requestConfirmation = (dialog: ConfirmationDialogState) => {
    setConfirmationDialog(dialog)
  }

  const closeConfirmation = () => {
    setConfirmationDialog(null)
  }

  const resetMigrationAfterAbort = () => {
    setSources([])
    setSelectedSource(null)
    setPrepareNotice('Migração abortada. A instância destino preparada foi removida; você pode preparar novamente do zero.')
    setSourceError(null)
    setDryRunReport(null)
    setDryRunJobId(null)
    setDryRunJobStatus(null)
    setDryRunJobError(null)
    setDryRunStdoutTail('')
    setDryRunStderrTail('')
    setDryRunAt(null)
    resetApply()
    setHandoffMd(null)
    setHandoffId(null)
    resetTargetOperations()
    resetCutoverChecklist()
  }

  const fetchSources = async () => {
    if (!targetSlug || !targetOrgSlug) return false
    setLoadingSources(true)
    setSourceError(null)
    try {
      const qs = new URLSearchParams({ target_machine_slug: targetSlug, target_org_slug: targetOrgSlug })
      const res = await fetch(`/cc/instances/${worker.id}/migration_sources?${qs.toString()}`, {
        headers: { Accept: 'application/json' },
      })
      const data = await parseJsonResponse(res)
      if (!res.ok || data.ok !== true) throw new Error(responseError(data, `broker respondeu ${res.status}`))
      const nextSources = (Array.isArray(data.sources) ? data.sources : []) as MigrationSource[]
      setSources(nextSources)
      if (nextSources.length > 0) setPrepareNotice(null)
      setSelectedSource((current) => {
        if (current && nextSources.some((s) => s.relative_path === current.relative_path)) return current
        return nextSources[0] || null
      })
      return nextSources.length > 0
    } catch (e) {
      setSources([])
      setSelectedSource(null)
      setSourceError((e as Error).message)
      return false
    } finally {
      setLoadingSources(false)
    }
  }

  const pollSourcesAfterPrepare = async () => {
    for (let attempt = 0; attempt < 36; attempt += 1) {
      const ready = await fetchSources()
      if (ready) return
      await new Promise((resolve) => window.setTimeout(resolve, 5000))
    }
    setPrepareNotice('Preparação ainda em andamento ou falhou no worker. Confira /jobs ou /cc/audit-logs antes de rodar o dry-run.')
  }

  useEffect(() => {
    void fetchSources()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [targetSlug, targetOrgSlug])

  useEffect(() => {
    if (typeof window === 'undefined') return
    window.localStorage.setItem(
      migrationTargetStorageKey(worker.id),
      JSON.stringify({ targetSlug, targetOrgSlug }),
    )
  }, [worker.id, targetSlug, targetOrgSlug])

  const enqueuePrepareDestination = () => {
    if (!targetSlug || !targetOrgSlug) return
    setPreparing(true)
    setPrepareNotice(null)
    router.post(
      `/cc/instances/${worker.id}/prepare_migration`,
      { target_machine_slug: targetSlug, target_org_slug: targetOrgSlug },
      {
        preserveScroll: true,
        onSuccess: () => {
          setPrepareNotice('Preparação enfileirada. O destino será criado e os arquivos serão importados pela máquina destino.')
          void pollSourcesAfterPrepare()
        },
        onFinish: () => setPreparing(false),
      },
    )
  }

  const prepareDestination = () => {
    if (!targetSlug || !targetOrgSlug) return
    requestConfirmation({
      title: 'Criar destino de migração',
      description: `Criar destino para "${worker.instance_name}" em ${targetOrgSlug}/${worker.instance_name} na máquina "${targetSlug}"?`,
      details: (
        <ul className="list-disc pl-4 space-y-1">
          <li>Cria ou garante a instância destino com o mesmo instance_name.</li>
          <li>A máquina destino puxa config e workspace do source para migration-sources.</li>
          <li>Não muda o worker no Workstation agora.</li>
          <li>Não executa apply da migração. O apply e o cutover continuam nos próximos passos.</li>
        </ul>
      ),
      confirmLabel: hasSources ? 'Refazer preparação' : 'Preparar',
      onConfirm: enqueuePrepareDestination,
    })
  }

  const startDryRun = async () => {
    if (!selectedSource) return
    setRunning(true)
    setDryRunReport(null)
    setDryRunJobError(null)
    setDryRunJobStatus(null)
    setDryRunStdoutTail('')
    setDryRunStderrTail('')
    resetApply()
    setHandoffMd(null)
    setHandoffId(null)
    try {
      const res = await fetch(`/cc/instances/${worker.id}/migrate_dry_run`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': csrfToken() },
        body: JSON.stringify({
          source_org: selectedSource.org || '',
          source_name: selectedSource.name,
          source_relative_path: selectedSource.relative_path,
          include_secrets: includeSecrets,
          target_machine_slug: targetSlug,
          target_org_slug: targetOrgSlug,
        }),
      })
      const data = await parseJsonResponse(res)
      if (!res.ok || data.ok !== true) throw new Error(responseError(data, `broker respondeu ${res.status}`))
      const jobId = Number(data.job_id)
      if (!Number.isFinite(jobId)) throw new Error('broker não retornou job_id')
      setDryRunJobId(jobId)
      await pollJob(jobId)
    } catch (e) {
      setDryRunJobError((e as Error).message)
    } finally {
      setRunning(false)
    }
  }

  const pollJob = async (jobId: number) => {
    const terminal = new Set(['succeeded', 'failed', 'timed_out', 'rejected'])
    const qs = new URLSearchParams({ target_machine_slug: targetSlug, target_org_slug: targetOrgSlug })
    for (let attempt = 0; attempt < 180; attempt += 1) {
      const res = await fetch(`/cc/instances/${worker.id}/migrate_dry_run/${jobId}?${qs.toString()}`, {
        headers: { Accept: 'application/json' },
      })
      const data = await parseJsonResponse(res)
      if (!res.ok || data.ok !== true) throw new Error(responseError(data, `broker polling respondeu ${res.status}`))

      const job = (data.job || {}) as Record<string, unknown>
      const status = String(job.status || 'unknown')
      setDryRunJobStatus(status)
      setDryRunStdoutTail(String(job.stdout_tail || ''))
      setDryRunStderrTail(String(job.stderr_tail || ''))

      if (data.parsed_report) {
        setDryRunReport(data.parsed_report as Record<string, unknown>)
        setDryRunAt(typeof job.completed_at === 'string' ? job.completed_at : new Date().toISOString())
        return
      }

      if (terminal.has(status)) {
        setDryRunAt(typeof job.completed_at === 'string' ? job.completed_at : new Date().toISOString())
        if (status !== 'succeeded') {
          setDryRunJobError(String(job.stderr_tail || `job ${status}`))
        } else if (data.parse_error) {
          setDryRunJobError(String(data.parse_error))
        }
        return
      }

      await new Promise((resolve) => window.setTimeout(resolve, 2000))
    }

    setDryRunJobError('timeout aguardando job do broker')
  }

  const parseStdoutAsReport = () => {
    try {
      setDryRunReport(JSON.parse(dryRunStdoutTail))
      setDryRunJobError(null)
    } catch (e) {
      setDryRunJobError(`stdout não é JSON válido: ${(e as Error).message}`)
    }
  }

  const dryRunApplyBlockers = () => {
    if (!dryRunReport) return ['dry-run ainda não concluído']
    const summary = (dryRunReport.summary as Record<string, number> | undefined) || {}
    const validation = (dryRunReport.validation as Record<string, unknown> | undefined) || {}
    const items = Array.isArray(dryRunReport.items) ? dryRunReport.items as Array<Record<string, unknown>> : []
    const blockers: string[] = []

    if ((summary.errors || 0) > 0) blockers.push(`dry-run tem ${summary.errors} erro(s)`)
    if ((summary.conflicts || 0) > 0) blockers.push(`dry-run tem ${summary.conflicts} conflito(s)`)
    if (validation.status && validation.status !== 'passed') blockers.push(`validation=${String(validation.status)}`)

    const reviewItems = items.filter((item) => {
      const questions = Array.isArray(item.llmReviewQuestions) ? item.llmReviewQuestions : []
      const parserStatus = (item.configGapReview as Record<string, unknown> | undefined)?.parserStatus
      return item.status === 'conflict'
        || item.status === 'error'
        || questions.length > 0
        || parserStatus === 'invalid-json'
    })
    if (reviewItems.length > 0) blockers.push(`${reviewItems.length} item(ns) exigem revisão do operador`)

    return blockers
  }

  const applyFailureContext = () => {
    const failedStatus = ['failed', 'timed_out', 'rejected'].includes(String(applyState.status || ''))
    if (!applyState.error && !failedStatus) return null

    return {
      job_id: applyState.jobId,
      status: applyState.status,
      command: applyState.command,
      error: applyState.error,
      stdout_tail: applyState.stdoutTail,
      stderr_tail: applyState.stderrTail,
      started_at: applyState.startedAt,
      completed_at: applyState.completedAt,
    }
  }

  const generateHandoff = async () => {
    if (!selectedSource) return
    const summary = (dryRunReport?.summary as Record<string, unknown>) || {}
    const validation = (dryRunReport?.validation as Record<string, unknown>) || {}
    const items = (dryRunReport?.items as Array<Record<string, unknown>>) || []
    const conflicts = items.filter((i) => i.status === 'conflict')

    try {
      const res = await fetch(`/cc/instances/${worker.id}/generate_handoff`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': csrfToken() },
        body: JSON.stringify({
          source_path: selectedSource.container_workspace_path,
          approved_scope: scope,
          include_secrets: includeSecrets,
          dry_run_at: dryRunAt,
          dry_run_job_id: dryRunJobId,
          dry_run_summary: summary,
          dry_run_validation: validation,
          dry_run_conflicts: conflicts,
          apply_failure: applyFailureContext(),
          target_machine_slug: targetSlug,
          target_org_slug: targetOrgSlug,
        }),
      })
      const data = await parseJsonResponse(res)
      if (!res.ok || data.ok !== true) {
        setDryRunJobError(responseError(data, `falha ao gerar handoff (${res.status})`))
        return
      }
      setHandoffMd(String(data.markdown || ''))
      setHandoffId(String(data.handoff_id || ''))
    } catch (e) {
      setDryRunJobError((e as Error).message)
    }
  }

  const enqueueApplyMigration = async () => {
    if (!selectedSource) return
    setApplying(true)
    setApplyState({
      ...EMPTY_MIGRATION_APPLY,
      status: 'starting',
      command: `openclaw ${worker.instance_name} migrate apply`,
      startedAt: new Date().toISOString(),
    })
    setHandoffMd(null)
    setHandoffId(null)

    try {
      const res = await fetch(`/cc/instances/${worker.id}/migrate_apply`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': csrfToken() },
        body: JSON.stringify({
          source_path: selectedSource.container_workspace_path,
          include_secrets: includeSecrets,
          target_machine_slug: targetSlug,
          target_org_slug: targetOrgSlug,
        }),
      })
      const data = await parseJsonResponse(res)
      if (!res.ok || data.ok !== true) throw new Error(responseError(data, `apply respondeu ${res.status}`))
      const jobId = Number(data.job_id)
      if (!Number.isFinite(jobId)) throw new Error('broker não retornou job_id')
      setApplyState((current) => ({
        ...current,
        jobId,
        status: 'queued',
        command: String(data.command || current.command || ''),
      }))
      await pollApplyJob(jobId)
    } catch (e) {
      setApplyState((current) => ({
        ...current,
        error: (e as Error).message,
        status: current.status || 'failed',
        completedAt: new Date().toISOString(),
      }))
    } finally {
      setApplying(false)
    }
  }

  const startApplyMigration = () => {
    if (!selectedSource || applying) return
    const blockers = dryRunApplyBlockers()
    if (blockers.length > 0) {
      setDryRunJobError(`Apply bloqueado: ${blockers.join('; ')}`)
      return
    }

    requestConfirmation({
      title: 'Aplicar migração',
      description: `Aplicar a migração deterministicamente em ${targetOrgSlug}/${worker.instance_name} na máquina "${targetSlug}"?`,
      details: (
        <div className="space-y-2">
          <p>Use este caminho somente para dry-run verde: sem errors, sem conflicts, validation passed e sem perguntas de revisão.</p>
          <p>O Workstation enfileira o comando no broker destino e acompanha o job. Cutover continua separado.</p>
        </div>
      ),
      confirmLabel: 'Aplicar',
      onConfirm: () => { void enqueueApplyMigration() },
    })
  }

  const pollApplyJob = async (jobId: number) => {
    const terminal = new Set(['succeeded', 'failed', 'timed_out', 'rejected'])
    const qs = new URLSearchParams({ target_machine_slug: targetSlug, target_org_slug: targetOrgSlug })
    for (let attempt = 0; attempt < 360; attempt += 1) {
      const res = await fetch(`/cc/instances/${worker.id}/migration_jobs/${jobId}?${qs.toString()}`, {
        headers: { Accept: 'application/json' },
      })
      const data = await parseJsonResponse(res)
      if (!res.ok || data.ok !== true) throw new Error(responseError(data, `broker polling respondeu ${res.status}`))

      const job = (data.job || {}) as Record<string, unknown>
      const status = String(job.status || 'unknown')
      const error = terminal.has(status) && status !== 'succeeded'
        ? String(job.stderr_tail || `job ${status}`)
        : null

      setApplyState((current) => ({
        ...current,
        jobId,
        status,
        stdoutTail: String(job.stdout_tail || ''),
        stderrTail: String(job.stderr_tail || ''),
        error,
        startedAt: typeof job.started_at === 'string' ? job.started_at : current.startedAt,
        completedAt: typeof job.completed_at === 'string' ? job.completed_at : current.completedAt,
      }))

      if (terminal.has(status)) return
      await new Promise((resolve) => window.setTimeout(resolve, 2000))
    }

    setApplyState((current) => ({
      ...current,
      error: 'timeout aguardando job do broker',
      completedAt: new Date().toISOString(),
    }))
  }

  const copyHandoff = async () => {
    if (!handoffMd) return
    await navigator.clipboard.writeText(handoffMd)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  const executeTargetOperation = async (key: TargetOperationKey) => {
    if (!targetSlug || !targetOrgSlug) return
    const meta = TARGET_OPERATION_META[key]

    setTargetOperationRunning(key)
    updateTargetOperation(key, {
      jobId: null,
      status: 'starting',
      stdoutTail: '',
      stderrTail: '',
      error: null,
      command: meta.command,
    })

    try {
      const res = await fetch(`/cc/instances/${worker.id}/${meta.endpoint}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': csrfToken() },
        body: JSON.stringify({ target_machine_slug: targetSlug, target_org_slug: targetOrgSlug }),
      })
      const data = await parseJsonResponse(res)
      if (!res.ok || data.ok !== true) throw new Error(responseError(data, `broker respondeu ${res.status}`))
      const jobId = Number(data.job_id)
      if (!Number.isFinite(jobId)) throw new Error('broker não retornou job_id')
      updateTargetOperation(key, { jobId, status: 'queued', command: String(data.command || meta.command) })
      await pollTargetOperationJob(key, jobId)
    } catch (e) {
      updateTargetOperation(key, { error: (e as Error).message })
    } finally {
      setTargetOperationRunning(null)
    }
  }

  const startTargetOperation = (key: TargetOperationKey) => {
    if (!targetSlug || !targetOrgSlug) return
    const meta = TARGET_OPERATION_META[key]
    const descriptions: Record<TargetOperationKey, string> = {
      doctor_check: `Rodar "${meta.command}" em ${targetSlug}/${targetOrgSlug}/${worker.instance_name}? Esse check não aplica fix; ele só mostra o diagnóstico do OpenClaw.`,
      doctor_repair: `Rodar "${meta.command}" em ${targetSlug}/${targetOrgSlug}/${worker.instance_name}? Isso aplica o doctor fix nativo do OpenClaw no destino antes do cutover.`,
      start: `Iniciar "${worker.instance_name}" em ${targetSlug}/${targetOrgSlug}?`,
      stop: `Parar "${worker.instance_name}" em ${targetSlug}/${targetOrgSlug}? Isso para o destino preparado, não a instância antiga.`,
      restart: `Reiniciar "${worker.instance_name}" em ${targetSlug}/${targetOrgSlug}? Use isso para checar o destino depois do apply.`,
      abort: `Abortar a migração de "${worker.instance_name}" em ${targetSlug}/${targetOrgSlug}? Isso remove a instância destino preparada e seus dados de staging. Não altera a instância antiga nem o worker no Workstation.`,
    }

    requestConfirmation({
      title: meta.title,
      description: descriptions[key],
      confirmLabel: key === 'abort' ? 'Abortar' : 'Executar',
      destructive: key === 'abort',
      onConfirm: () => { void executeTargetOperation(key) },
    })
  }

  const pollTargetOperationJob = async (key: TargetOperationKey, jobId: number) => {
    const terminal = new Set(['succeeded', 'failed', 'timed_out', 'rejected'])
    const qs = new URLSearchParams({ target_machine_slug: targetSlug, target_org_slug: targetOrgSlug })
    for (let attempt = 0; attempt < 360; attempt += 1) {
      const res = await fetch(`/cc/instances/${worker.id}/migration_jobs/${jobId}?${qs.toString()}`, {
        headers: { Accept: 'application/json' },
      })
      const data = await parseJsonResponse(res)
      if (!res.ok || data.ok !== true) throw new Error(responseError(data, `broker polling respondeu ${res.status}`))

      const job = (data.job || {}) as Record<string, unknown>
      const status = String(job.status || 'unknown')
      updateTargetOperation(key, {
        status,
        stdoutTail: String(job.stdout_tail || ''),
        stderrTail: String(job.stderr_tail || ''),
        error: terminal.has(status) && status !== 'succeeded'
          ? String(job.stderr_tail || `job ${status}`)
          : null,
      })

      if (terminal.has(status) && key === 'abort' && status === 'succeeded') {
        resetMigrationAfterAbort()
        return
      }
      if (terminal.has(status)) return

      await new Promise((resolve) => window.setTimeout(resolve, 2000))
    }

    updateTargetOperation(key, { error: 'timeout aguardando job do broker' })
  }

  const runCutoverCheck = async () => {
    if (!targetSlug || !targetOrgSlug) return null
    setCutoverChecking(true)
    setCutoverCheckError(null)
    try {
      const qs = new URLSearchParams({ target_machine_slug: targetSlug, target_org_slug: targetOrgSlug })
      const res = await fetch(`/cc/instances/${worker.id}/migration_cutover_check?${qs.toString()}`, {
        headers: { Accept: 'application/json' },
      })
      const data = await parseJsonResponse(res)
      if (!res.ok || data.ok !== true) throw new Error(responseError(data, `broker respondeu ${res.status}`))
      const checklist = (data.checklist || null) as CutoverChecklist | null
      setCutoverChecklist(checklist)
      return checklist
    } catch (e) {
      setCutoverChecklist(null)
      setCutoverCheckError((e as Error).message)
      return null
    } finally {
      setCutoverChecking(false)
    }
  }

  const pollFinalizeRun = async (runId: string) => {
    const terminal = new Set(['succeeded', 'failed'])
    for (let attempt = 0; attempt < 360; attempt += 1) {
      const qs = new URLSearchParams({ cutover_run_id: runId })
      const res = await fetch(`/cc/instances/${worker.id}/migration_finalize_status?${qs.toString()}`, {
        headers: { Accept: 'application/json' },
      })
      const data = await parseJsonResponse(res)
      if (!res.ok || data.ok !== true) throw new Error(responseError(data, `status respondeu ${res.status}`))

      const status = String(data.status || 'unknown')
      const events = (Array.isArray(data.events) ? data.events : []) as FinalizeEvent[]
      setFinalizeRun((current) => ({
        ...current,
        status,
        events,
        error: status === 'failed'
          ? String(((data.latest as Record<string, unknown> | undefined)?.error) || 'cutover falhou')
          : null,
      }))

      if (terminal.has(status)) {
        setFinalizing(false)
        return
      }

      await new Promise((resolve) => window.setTimeout(resolve, 2000))
    }

    setFinalizeRun((current) => ({ ...current, error: 'timeout aguardando status do cutover' }))
    setFinalizing(false)
  }

  const submitFinalizeMigration = async (bypassCutoverValidation: boolean) => {
    setFinalizing(true)
    setFinalizeRun({ ...EMPTY_FINALIZE_RUN, status: 'enqueueing' })
    try {
      const res = await fetch(`/cc/instances/${worker.id}/finalize_migration`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-CSRF-Token': csrfToken() },
        body: JSON.stringify({
          target_machine_slug: targetSlug,
          target_org_slug: targetOrgSlug,
          stop_previous_instance: true,
          bypass_cutover_validation: bypassCutoverValidation,
          bypass_slack_probe: bypassSlackProbe,
        }),
      })
      const data = await parseJsonResponse(res)
      if (!res.ok || data.ok !== true) throw new Error(responseError(data, `finalize respondeu ${res.status}`))
      const runId = String(data.cutover_run_id || '')
      if (!runId) throw new Error('finalize não retornou cutover_run_id')
      setFinalizeRun({
        runId,
        activeJobId: data.active_job_id ? String(data.active_job_id) : null,
        status: String(data.status || 'queued'),
        events: [],
        error: null,
      })
      await pollFinalizeRun(runId)
    } catch (e) {
      setFinalizeRun((current) => ({ ...current, error: (e as Error).message, status: 'failed' }))
      setFinalizing(false)
    }
  }

  const finalizeMigration = async () => {
    if (!targetSlug || !targetOrgSlug || finalizing) return
    const checklist = await runCutoverCheck()
    if (!checklist) return

    const blockers = checklist.items.filter((item) => item.status === 'blocked')
    const nonBypassable = blockers.filter((item) => item.bypassable === false)
    if (nonBypassable.length > 0) {
      requestConfirmation({
        title: 'Cutover bloqueado',
        description: 'O checklist encontrou blockers que não podem ser bypassados.',
        details: (
          <ul className="list-disc pl-4 space-y-1 break-words [overflow-wrap:anywhere]">
            {nonBypassable.map((item) => <li key={item.id}>{item.message}</li>)}
          </ul>
        ),
        confirmLabel: 'OK',
        cancelLabel: null,
      })
      return
    }

    const bypassCutoverValidation = blockers.length > 0
    if (blockers.length > 0) {
      requestConfirmation({
        title: 'Continuar com bypass',
        description: `Checklist encontrou ${blockers.length} blocker(s) bypassable. Continuar mesmo assim?`,
        details: (
          <ul className="list-disc pl-4 space-y-1 break-words [overflow-wrap:anywhere]">
            {blockers.slice(0, 8).map((item) => <li key={item.id}>{item.message}</li>)}
            {blockers.length > 8 ? <li>...</li> : null}
          </ul>
        ),
        confirmLabel: 'Continuar com bypass',
        destructive: true,
        onConfirm: () => submitFinalizeMigration(true),
      })
      return
    }

    requestConfirmation({
      title: 'Finalizar migração',
      description: `Finalizar migração de "${worker.instance_name}" para a máquina "${targetSlug}"?`,
      details: (
        <div className="space-y-2">
          <p>Faça isso somente depois que o apply concluiu no destino e o report foi validado.</p>
          <p>O Workstation valida o destino, para a instância antiga e enfileira o cutover em background para evitar timeout.</p>
          <p>O cutover usa a ordem segura: parar antiga, preparar destino com stop/start, rodar probes de canais runtime e só então trocar o banco.</p>
          {bypassSlackProbe ? (
            <p>Bypass de Slack probe ativo: se houver Slack Socket Mode, o destino ainda será preparado. WhatsApp Cloud continua sendo validado depois do restart.</p>
          ) : null}
        </div>
      ),
      confirmLabel: 'Finalizar',
      onConfirm: () => submitFinalizeMigration(bypassCutoverValidation),
    })
  }

  const summary = (dryRunReport?.summary as Record<string, number> | undefined) || null
  const summaryTone: Tone = (summary?.errors || 0) > 0
    ? 'critical'
    : (summary?.conflicts || 0) > 0
      ? 'attention'
      : 'success'
  const applyBlockers = dryRunApplyBlockers()
  const applySucceeded = applyState.status === 'succeeded'
  const applyFailed = !!applyFailureContext()
  const applyTone: Tone = applyFailed
    ? 'critical'
    : applySucceeded
      ? 'success'
      : applyState.status
        ? 'info'
        : 'neutral'

  return (
    <div className="space-y-4">
      <section className="bg-card border border-border rounded-lg p-4 flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h3 className="text-sm font-semibold text-foreground">
            Migração para <code className="font-mono">{migrationTarget.version}</code>
          </h3>
          <p className="text-xs text-muted-foreground mt-1">
            Dry-run verde pode ser aplicado direto pelo Workstation. O handoff com a skill versionada fica para blockers ou falhas de apply: <code className="font-mono">{migrationTarget.skill_name}</code>{migrationTarget.skill_path ? <> · <code className="font-mono">{migrationTarget.skill_path}</code></> : null}.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {hasSources
            ? <Pill tone="success">Arquivos importados</Pill>
            : isLegacySource
              ? <Pill tone="warning">Legacy aguardando preparo</Pill>
              : <Pill tone="neutral">Aguardando preparo</Pill>}
        </div>
      </section>

      <div className="grid gap-3 md:grid-cols-5">
        <Stage step={1} title="Destino" state={hasSources ? 'done' : 'active'}>
          {hasSources ? 'preparado' : 'pendente'}
        </Stage>
        <Stage step={2} title="Arquivos" state={hasSources ? 'done' : 'idle'}>
          {hasSources ? 'importados' : 'pendente'}
        </Stage>
        <Stage step={3} title="Dry-run" state={dryRunReport ? 'done' : hasSources ? 'active' : 'idle'}>
          {dryRunReport ? 'ok' : 'pendente'}
        </Stage>
        <Stage step={4} title="Apply" state={applySucceeded ? 'done' : dryRunReport ? 'active' : 'idle'}>
          {applySucceeded ? 'aplicado' : applying ? 'rodando' : 'pendente'}
        </Stage>
        <Stage step={5} title="Cutover" state={applySucceeded ? 'active' : 'idle'}>
          manual
        </Stage>
      </div>

      <CardSection
        title="1. Criar destino e importar arquivos"
        action={
          targetSlug
            ? (
              <Button size="sm" variant="outline" onClick={fetchSources} disabled={loadingSources}>
                <RefreshCw className="h-4 w-4 mr-1" />
                {loadingSources ? 'Atualizando…' : 'Atualizar'}
              </Button>
            )
            : null
        }
      >
          {targets.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              Nenhuma FleetMachine destino disponível. Cadastre outra em <code className="font-mono">/cc/fleet_machines</code>.
            </p>
          ) : (
            <>
              <div className="grid gap-3 md:grid-cols-2">
                <label className="block">
                  <div className="text-xs uppercase tracking-wider text-muted-foreground mb-1">FleetMachine</div>
                  <select
                    value={targetSlug}
                    onChange={(e) => {
                      setTargetSlug(e.target.value)
                      setDryRunReport(null)
                      resetApply()
                      setHandoffMd(null)
                      resetTargetOperations()
                      resetCutoverChecklist()
                    }}
                    className="w-full py-2 px-3 text-sm border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-ring"
                  >
                    {targets.map((m) => (
                      <option key={m.slug} value={m.slug}>{m.name} ({m.slug})</option>
                    ))}
                  </select>
                </label>

                <label className="block">
                  <div className="text-xs uppercase tracking-wider text-muted-foreground mb-1">Organização destino</div>
                  <input
                    value={targetOrgSlug}
                    onChange={(e) => {
                      setTargetOrgSlug(e.target.value)
                      setDryRunReport(null)
                      resetApply()
                      setHandoffMd(null)
                      resetTargetOperations()
                      resetCutoverChecklist()
                    }}
                    className="w-full py-2 px-3 text-sm border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-ring font-mono"
                  />
                </label>
              </div>

              <div className="bg-muted/40 border border-border rounded-md px-3 py-2 text-xs space-y-1">
                <div>
                  <span className="text-muted-foreground">Destino: </span>
                  <code className="font-mono text-foreground">{targetOrgSlug || 'org'}/{worker.instance_name}</code>
                </div>
                <div className="text-muted-foreground">
                  A máquina destino puxa os arquivos da source via SSH para <code className="font-mono">migration-sources/</code>. O worker no Workstation só muda no passo final.
                </div>
                {hasSources && (
                  <div className="text-emerald-700 dark:text-emerald-300">
                    Source encontrada no destino: {sources.length} item{sources.length === 1 ? '' : 's'}.
                  </div>
                )}
                {prepareNotice && (
                  <div className="text-blue-700 dark:text-blue-300">
                    {prepareNotice}
                  </div>
                )}
                {sourceError && (
                  <div className="text-amber-700 dark:text-amber-300">
                    {sourceError}
                  </div>
                )}
              </div>

              <div className="flex justify-end gap-2">
                <Button onClick={prepareDestination} disabled={!targetSlug || !targetOrgSlug || preparing}>
                  <Server className="h-4 w-4 mr-1" />
                  {preparing ? 'Enfileirando…' : hasSources ? 'Refazer preparação' : 'Preparar migração'}
                </Button>
              </div>
            </>
          )}
      </CardSection>

      {hasSources && (
        <>
          <CardSection title="2. Arquivos importados">
            <p className="text-xs text-muted-foreground">
              Lida do broker destino <code className="font-mono">{targetSlug}</code>, não da máquina source.
            </p>
            <select
              value={selectedSource?.relative_path || ''}
              onChange={(e) => {
                setSelectedSource(sources.find((s) => s.relative_path === e.target.value) || null)
                setDryRunReport(null)
                resetApply()
                setHandoffMd(null)
              }}
              className="w-full py-2 px-3 text-sm border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-ring"
            >
              {visibleSources.map((s) => (
                <option key={s.relative_path} value={s.relative_path}>
                  {s.relative_path} {s.has_config ? '· com config' : '· sem config'} — {formatBytes(s.size_bytes)}
                </option>
              ))}
            </select>
            {selectedSource && (
              <div className="text-xs text-muted-foreground space-y-0.5">
                <div className="font-mono break-all">{selectedSource.container_workspace_path}</div>
                <div>capturado: {selectedSource.captured_at || '—'}</div>
              </div>
            )}
          </CardSection>

          <CardSection
            title="3. Dry-run"
            action={dryRunJobStatus
              ? <Pill tone={dryRunReport ? 'success' : dryRunJobError ? 'critical' : 'info'}>{dryRunJobStatus}</Pill>
              : null}
          >
            <p className="text-xs text-muted-foreground">
              provider <code className="font-mono">{migrationTarget.provider}</code> · plugin <code className="font-mono">{migrationTarget.plugin_id}</code>
            </p>

            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <div className="text-xs uppercase tracking-wider text-muted-foreground">Escopo aprovado</div>
                {APPROVED_SCOPES.map((s) => (
                  <label key={s.key} className="flex items-center gap-2 text-sm">
                    <Checkbox
                      checked={!!scope[s.key]}
                      onCheckedChange={(c) => setScope({ ...scope, [s.key]: c === true })}
                    />
                    {s.label}
                  </label>
                ))}
              </div>

              <div className="space-y-2">
                <div className="text-xs uppercase tracking-wider text-muted-foreground">Secrets</div>
                <label className="flex items-center gap-2 text-sm">
                  <Checkbox checked={includeSecrets} onCheckedChange={(c) => setIncludeSecrets(c === true)} />
                  Incluir secrets na migração
                </label>
              </div>
            </div>

            <div className="flex items-center justify-between gap-3">
              <div className="text-xs text-muted-foreground">
                {dryRunJobId ? <>job <code className="font-mono">{dryRunJobId}</code></> : 'Nenhum dry-run nesta sessão.'}
              </div>
              <Button onClick={startDryRun} disabled={!selectedSource || running}>
                <Play className="h-4 w-4 mr-1" />
                {running ? 'Rodando…' : 'Rodar dry-run'}
              </Button>
            </div>

            {dryRunJobError && (
              <div className="bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-500/20 rounded-md px-3 py-2 text-sm text-red-700 dark:text-red-300 inline-flex items-start gap-2">
                <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
                <span>{dryRunJobError}</span>
              </div>
            )}

            {dryRunReport && (
              <div className="bg-muted/40 border border-border rounded-md px-3 py-2 text-sm space-y-2">
                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <Pill tone={summaryTone}>report parseado</Pill>
                  {summary && (
                    <span className="text-xs text-muted-foreground">
                      {summary.migrated ?? 0} migrated · {summary.skipped ?? 0} skipped · {summary.conflicts ?? 0} conflicts · {summary.errors ?? 0} errors
                    </span>
                  )}
                </div>
                <details>
                  <summary className="cursor-pointer text-xs font-medium">Report JSON</summary>
                  <pre className="text-xs bg-background rounded p-2 overflow-x-auto max-h-[360px] mt-2 font-mono">
                    {JSON.stringify(dryRunReport, null, 2)}
                  </pre>
                </details>
              </div>
            )}

            {(dryRunStdoutTail || dryRunStderrTail) && !dryRunReport && (
              <details className="bg-muted/30 border border-border/60 rounded-md p-3">
                <summary className="cursor-pointer text-xs font-medium">Output do job</summary>
                <div className="space-y-2 mt-2">
                  {dryRunStderrTail && (
                    <pre className="font-mono text-xs bg-background rounded p-2 overflow-x-auto whitespace-pre-wrap">{dryRunStderrTail}</pre>
                  )}
                  {dryRunStdoutTail && (
                    <>
                      <pre className="font-mono text-xs bg-background rounded p-2 overflow-x-auto whitespace-pre-wrap">{dryRunStdoutTail}</pre>
                      <Button size="sm" variant="outline" onClick={parseStdoutAsReport}>Usar stdout como report</Button>
                    </>
                  )}
                </div>
              </details>
            )}
          </CardSection>

          <CardSection
            title="4. Apply"
            action={applyState.status ? <Pill tone={applyTone}>{applyState.status}</Pill> : null}
          >
            <p className="text-xs text-muted-foreground">
              Dry-run verde pode ser aplicado direto pelo Workstation. Se o apply falhar, gere um handoff com o erro do job para o agente corrigir o blocker.
            </p>

            {dryRunReport && applyBlockers.length > 0 && (
              <div className="bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-500/20 rounded-md px-3 py-2 text-sm text-amber-800 dark:text-amber-300 flex items-start gap-2 max-w-full">
                <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
                <span className="min-w-0 break-words [overflow-wrap:anywhere]">
                  Apply direto bloqueado: {applyBlockers.join('; ')}
                </span>
              </div>
            )}

            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div className="text-xs text-muted-foreground">
                {applyState.jobId ? <>apply job <code className="font-mono">{applyState.jobId}</code></> : 'Nenhum apply nesta sessão.'}
              </div>
              <div className="flex items-center gap-2 flex-wrap justify-end">
                <Button onClick={generateHandoff} variant="outline" disabled={!dryRunReport || !selectedSource}>
                  <WandSparkles className="h-4 w-4 mr-1" />
                  {applyFailed ? 'Gerar handoff de correção' : 'Gerar handoff'}
                </Button>
                <Button onClick={startApplyMigration} disabled={!selectedSource || !dryRunReport || applying || applyBlockers.length > 0}>
                  <Play className="h-4 w-4 mr-1" />
                  {applying ? 'Aplicando…' : applySucceeded ? 'Aplicado' : 'Aplicar migração'}
                </Button>
              </div>
            </div>

            {(applyState.error || applyFailed) && (
              <div className="bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-500/20 rounded-md px-3 py-2 text-sm text-red-700 dark:text-red-300 flex items-start gap-2 max-w-full">
                <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
                <span className="min-w-0 break-words [overflow-wrap:anywhere]">
                  {applyState.error || 'apply falhou'}
                </span>
              </div>
            )}

            {applySucceeded && (
              <div className="bg-green-50 dark:bg-green-950/30 border border-green-200 dark:border-green-500/20 rounded-md px-3 py-2 text-sm text-green-700 dark:text-green-300">
                Apply concluído no destino. Revise o output se necessário e siga para o checklist de cutover.
              </div>
            )}

            {(applyState.stdoutTail || applyState.stderrTail) && (
              <details className="bg-muted/30 border border-border/60 rounded-md p-3">
                <summary className="cursor-pointer text-xs font-medium">Output do apply</summary>
                <div className="space-y-2 mt-2 min-w-0">
                  {applyState.stderrTail && (
                    <pre className="font-mono text-xs bg-background rounded p-2 overflow-x-auto whitespace-pre-wrap break-words [overflow-wrap:anywhere]">{applyState.stderrTail}</pre>
                  )}
                  {applyState.stdoutTail && (
                    <pre className="font-mono text-xs bg-background rounded p-2 overflow-x-auto whitespace-pre-wrap break-words [overflow-wrap:anywhere]">{applyState.stdoutTail}</pre>
                  )}
                </div>
              </details>
            )}

            {handoffMd && (
              <div className="space-y-2">
                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <span className="text-xs text-muted-foreground break-all">
                    handoff id <code className="font-mono">{handoffId}</code>
                  </span>
                  <Button size="sm" variant="outline" onClick={copyHandoff}>
                    <Copy className="h-4 w-4 mr-1" />
                    {copied ? 'Copiado' : 'Copiar'}
                  </Button>
                </div>
                <pre className="text-xs bg-muted/40 rounded p-3 overflow-x-auto max-h-[500px] whitespace-pre-wrap font-mono break-words [overflow-wrap:anywhere]">
                  {handoffMd}
                </pre>
              </div>
            )}
          </CardSection>

          <CardSection title="5. Finalizar no Workstation">
            <p className="text-xs text-muted-foreground">
              Depois que o apply concluir e o destino estiver validado, este passo troca o apontamento do worker para <code className="font-mono">{targetSlug}</code>. O nome da instância e o worker id continuam iguais.
            </p>
            <div className="bg-muted/40 border border-border rounded-md px-3 py-2 text-xs text-muted-foreground">
              A validação exige que a instância exista no broker destino, <code className="font-mono">bootstrap_applied</code> esteja verdadeiro e integrações conectadas não estejam desativadas. O cutover para a instância antiga, prepara o destino com stop/start para carregar config/canais runtime e só depois roda probes de canais como Slack Socket Mode e WhatsApp Cloud. Para rollback, o apontamento pode ser voltado para a máquina anterior.
            </div>
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div className="text-xs text-muted-foreground">
                Rode o checklist antes do cutover. Blockers podem ser bypassados com confirmação explícita, exceto <code className="font-mono">bootstrap_applied=false</code>.
              </div>
              <Button variant="outline" onClick={runCutoverCheck} disabled={cutoverChecking}>
                <Check className="h-4 w-4 mr-1" />
                {cutoverChecking ? 'Checando…' : 'Checklist cutover'}
              </Button>
            </div>
            {cutoverCheckError && (
              <div className="bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-500/20 rounded-md px-3 py-2 text-sm text-red-700 dark:text-red-300 flex items-start gap-2 max-w-full">
                <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
                <span className="min-w-0 break-words [overflow-wrap:anywhere]">{cutoverCheckError}</span>
              </div>
            )}
            {cutoverChecklist && <CutoverChecklistPanel checklist={cutoverChecklist} />}
            <label className="flex items-start gap-2 rounded-md border border-border bg-muted/30 px-3 py-2 text-sm">
              <Checkbox
                checked={bypassSlackProbe}
                onCheckedChange={(checked) => setBypassSlackProbe(checked === true)}
              />
              <span>
                <span className="font-medium text-foreground">Bypass Slack probe</span>
                <span className="block text-xs text-muted-foreground">
                  Se houver Slack Socket Mode, o cutover ainda para a antiga e prepara o destino, mas não bloqueia na etapa de probe/live check de Slack. WhatsApp Cloud continua sendo validado depois do restart.
                </span>
              </span>
            </label>
            <div className="grid gap-2 md:grid-cols-3 xl:grid-cols-6">
              <Button
                variant="outline"
                onClick={() => startTargetOperation('doctor_check')}
                disabled={targetOperationRunning !== null}
              >
                <Wrench className="h-4 w-4 mr-1" />
                Doctor check
              </Button>
              <Button
                variant="outline"
                onClick={() => startTargetOperation('doctor_repair')}
                disabled={targetOperationRunning !== null}
              >
                <Wrench className="h-4 w-4 mr-1" />
                Doctor repair
              </Button>
              <Button
                variant="outline"
                onClick={() => startTargetOperation('start')}
                disabled={targetOperationRunning !== null}
              >
                <Play className="h-4 w-4 mr-1" />
                Start
              </Button>
              <Button
                variant="outline"
                onClick={() => startTargetOperation('stop')}
                disabled={targetOperationRunning !== null}
              >
                <Square className="h-4 w-4 mr-1" />
                Stop
              </Button>
              <Button
                variant="outline"
                onClick={() => startTargetOperation('restart')}
                disabled={targetOperationRunning !== null}
              >
                <RefreshCw className="h-4 w-4 mr-1" />
                Restart destino
              </Button>
              <Button
                variant="destructive"
                onClick={() => startTargetOperation('abort')}
                disabled={targetOperationRunning !== null}
              >
                <Trash2 className="h-4 w-4 mr-1" />
                Abortar
              </Button>
            </div>
            <div className="space-y-2">
              {TARGET_OPERATION_KEYS.map((key) => (
                <TargetOperationResult
                  key={key}
                  title={TARGET_OPERATION_META[key].title}
                  state={targetOperations[key]}
                />
              ))}
            </div>
            <div className="flex justify-end">
              <Button onClick={finalizeMigration} disabled={!targetSlug || !targetOrgSlug || finalizing}>
                <Check className="h-4 w-4 mr-1" />
                {finalizing ? 'Finalizando…' : 'Finalizar migração'}
              </Button>
            </div>
            <FinalizeRunPanel run={finalizeRun} />
          </CardSection>
        </>
      )}
      <MigrationConfirmationDialog dialog={confirmationDialog} onClose={closeConfirmation} />
    </div>
  )
}

function MigrationConfirmationDialog({
  dialog,
  onClose,
}: {
  dialog: ConfirmationDialogState | null
  onClose: () => void
}) {
  return (
    <AlertDialog open={dialog !== null} onOpenChange={(open) => { if (!open) onClose() }}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{dialog?.title || 'Confirmar'}</AlertDialogTitle>
          <AlertDialogDescription>{dialog?.description}</AlertDialogDescription>
        </AlertDialogHeader>
        {dialog?.details ? (
          <div className="rounded-md border border-border bg-muted/30 p-3 text-sm text-muted-foreground break-words [overflow-wrap:anywhere]">
            {dialog.details}
          </div>
        ) : null}
        <AlertDialogFooter>
          {dialog?.cancelLabel !== null ? (
            <AlertDialogCancel onClick={onClose}>{dialog?.cancelLabel || 'Cancelar'}</AlertDialogCancel>
          ) : null}
          <AlertDialogAction
            className={dialog?.destructive ? 'bg-destructive text-destructive-foreground hover:bg-destructive/90' : undefined}
            onClick={() => {
              const onConfirm = dialog?.onConfirm
              onClose()
              onConfirm?.()
            }}
          >
            {dialog?.confirmLabel || 'OK'}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  )
}

function FinalizeRunPanel({ run }: { run: FinalizeRunState }) {
  if (!run.runId && !run.status && !run.error) return null

  const tone: Tone = run.error || run.status === 'failed'
    ? 'critical'
    : run.status === 'succeeded'
      ? 'success'
      : 'info'
  const workflowSteps = mergeFinalizeWorkflow(run.events)

  return (
    <div className="bg-muted/30 border border-border rounded-md p-3 space-y-3 min-w-0">
      <div className="flex items-center justify-between gap-3 flex-wrap min-w-0">
        <div className="text-sm font-medium text-foreground min-w-0">Execução do cutover</div>
        <div className="flex items-center gap-2 flex-wrap justify-end min-w-0">
          <Pill tone={tone}>{run.status || 'pending'}</Pill>
          {run.runId ? <span className="text-xs text-muted-foreground break-all">run <code className="font-mono">{run.runId.slice(0, 8)}</code></span> : null}
          {run.activeJobId ? <span className="text-xs text-muted-foreground break-all">job <code className="font-mono">{run.activeJobId}</code></span> : null}
        </div>
      </div>
      {run.error && (
        <div className="bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-500/20 rounded-md px-3 py-2 text-sm text-red-700 dark:text-red-300 flex items-start gap-2 max-w-full">
          <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
          <span className="min-w-0 break-words [overflow-wrap:anywhere]">{run.error}</span>
        </div>
      )}
      {workflowSteps.length > 0 ? (
        <div className="space-y-1 min-w-0">
          {workflowSteps.map((step) => {
            const eventTone: Tone = step.error || step.status === 'failed'
              ? 'critical'
              : step.status === 'bypassed'
                ? 'warning'
              : step.status === 'passed' || step.status === 'succeeded'
                ? 'success'
                : 'info'
            return (
              <details key={step.key} className="bg-background/60 border border-border/60 rounded px-3 py-2 min-w-0">
                <summary className="cursor-pointer text-xs font-medium flex items-center gap-2 flex-wrap min-w-0">
                  <Pill tone={eventTone}>{step.status || 'event'}</Pill>
                  <span className="min-w-0 break-words [overflow-wrap:anywhere]">{step.title}</span>
                  {step.message ? <span className="text-muted-foreground font-normal hidden md:inline min-w-0 break-words [overflow-wrap:anywhere]">{step.message}</span> : null}
                  {step.at ? <span className="text-muted-foreground ml-auto">{formatDateTime(step.at)}</span> : null}
                </summary>
                <div className="mt-2 space-y-2 text-xs text-muted-foreground min-w-0">
                  <div className="break-words [overflow-wrap:anywhere]">step <code className="font-mono">{step.key}</code></div>
                  {step.event ? <div className="break-words [overflow-wrap:anywhere]">event <code className="font-mono">{step.event}</code></div> : null}
                  {step.error ? <div className="text-red-700 dark:text-red-300 break-words [overflow-wrap:anywhere]">{step.error}</div> : null}
                  {step.history.length > 1 ? (
                    <div className="flex flex-wrap gap-1">
                      {step.history.map((entry, index) => (
                        <Pill
                          key={entry.id || `${step.key}-${index}`}
                          tone={entry.error || entry.status === 'failed'
                            ? 'critical'
                            : entry.status === 'bypassed'
                              ? 'warning'
                              : entry.status === 'passed' || entry.status === 'succeeded'
                                ? 'success'
                                : 'info'}
                        >
                          {entry.status || entry.event || 'event'}
                        </Pill>
                      ))}
                    </div>
                  ) : null}
                  {step.details ? (
                    <pre className="font-mono bg-muted rounded p-2 overflow-x-auto whitespace-pre-wrap break-words [overflow-wrap:anywhere] max-w-full">
                      {JSON.stringify(step.details, null, 2)}
                    </pre>
                  ) : null}
                </div>
              </details>
            )
          })}
        </div>
      ) : (
        <div className="text-xs text-muted-foreground break-words [overflow-wrap:anywhere]">Aguardando o job registrar a primeira etapa.</div>
      )}
    </div>
  )
}

function CutoverChecklistPanel({ checklist }: { checklist: CutoverChecklist }) {
  const blocked = checklist.items.filter((item) => item.status === 'blocked').length
  const warnings = checklist.items.filter((item) => item.status === 'warning').length
  const tone: Tone = blocked > 0 ? 'critical' : warnings > 0 ? 'warning' : 'success'

  return (
    <div className="bg-muted/30 border border-border rounded-md p-3 space-y-3 min-w-0">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="text-sm font-medium text-foreground">Checklist de cutover</div>
        <div className="flex items-center gap-2">
          <Pill tone={tone}>{blocked} blockers</Pill>
          <Pill tone={warnings > 0 ? 'warning' : 'success'}>{warnings} warnings</Pill>
        </div>
      </div>
      <div className="space-y-1">
        {checklist.items.map((item) => (
          <div key={item.id} className="flex items-start gap-2 text-xs min-w-0">
            <Pill tone={item.status === 'blocked' ? 'critical' : item.status === 'warning' ? 'warning' : 'success'}>
              {item.status}
            </Pill>
            <span className="text-muted-foreground min-w-0 break-words [overflow-wrap:anywhere]">{item.message}</span>
            {item.status === 'blocked' && item.bypassable !== false && (
              <span className="text-amber-700 dark:text-amber-300 shrink-0">bypass possível</span>
            )}
          </div>
        ))}
      </div>
    </div>
  )
}

function TargetOperationResult({ title, state }: { title: string; state: TargetOperationState }) {
  if (!state.jobId && !state.status && !state.error) return null

  const tone: Tone = state.error
    ? 'critical'
    : state.status === 'succeeded'
      ? 'success'
      : state.status === 'failed' || state.status === 'timed_out' || state.status === 'rejected'
        ? 'critical'
        : 'info'

  return (
    <details className="bg-muted/30 border border-border/60 rounded-md p-3">
      <summary className="cursor-pointer text-xs font-medium flex items-center gap-2">
        <span>{title}</span>
        <Pill tone={tone}>{state.status || 'pending'}</Pill>
        {state.jobId ? <span className="text-muted-foreground">job {state.jobId}</span> : null}
      </summary>
      <div className="space-y-2 mt-2">
        {state.command && (
          <div className="text-xs text-muted-foreground">
            comando <code className="font-mono">{state.command}</code>
          </div>
        )}
        {state.error && (
          <div className="text-sm text-red-700 dark:text-red-300">{state.error}</div>
        )}
        {state.stderrTail && (
          <pre className="font-mono text-xs bg-background rounded p-2 overflow-x-auto whitespace-pre-wrap">{state.stderrTail}</pre>
        )}
        {state.stdoutTail && (
          <pre className="font-mono text-xs bg-background rounded p-2 overflow-x-auto whitespace-pre-wrap">{state.stdoutTail}</pre>
        )}
      </div>
    </details>
  )
}

function Stage({
  step,
  title,
  state,
  children,
}: {
  step: number
  title: string
  state: 'done' | 'active' | 'idle'
  children: ReactNode
}) {
  const iconClass = state === 'done'
    ? 'text-emerald-600 dark:text-emerald-400'
    : state === 'active'
      ? 'text-amber-600 dark:text-amber-400'
      : 'text-muted-foreground/50'
  const Icon = state === 'done' ? Check : state === 'active' ? Circle : Circle
  return (
    <div className="bg-card border border-border rounded-lg px-4 py-3">
      <div className="flex items-center justify-between gap-2">
        <div className="text-xs uppercase tracking-wider text-muted-foreground">
          Passo {step}
        </div>
        <Icon className={`h-4 w-4 ${iconClass}`} />
      </div>
      <div className="text-sm font-semibold text-foreground mt-1">{title}</div>
      <div className="text-xs text-muted-foreground">{children}</div>
    </div>
  )
}

function formatBytes(bytes: number) {
  if (!Number.isFinite(bytes) || bytes <= 0) return '0 B'
  const units = ['B', 'KB', 'MB', 'GB']
  let size = bytes
  let unit = 0
  while (size >= 1024 && unit < units.length - 1) {
    size /= 1024
    unit += 1
  }
  return `${size.toFixed(unit === 0 ? 0 : 1)} ${units[unit]}`
}

function formatDateTime(value: string) {
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return value
  return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' })
}

function migrationTargetStorageKey(workerId: string) {
  return `cc.instances.${workerId}.migrationTarget`
}

function readSavedTarget(workerId: string): { targetSlug: string; targetOrgSlug: string } | null {
  if (typeof window === 'undefined') return null
  try {
    const raw = window.localStorage.getItem(migrationTargetStorageKey(workerId))
    return raw ? JSON.parse(raw) : null
  } catch {
    return null
  }
}
